import { Analytics } from '@cobliteam/analytics';
import { useGetTriggersNotifications, useMarkAllNotificationsAsRead } from '@cobliteam/hooks/src/useEntity/useAlerts';
import { useTranslation } from '@cobliteam/i18n';

import { Button, Divider, Loading, Typography } from '@cobliteam/ui/components';
import Alert from '@cobliteam/ui/components/Alert';
import Icon from '@cobliteam/ui/icons';
import { endOfDay, startOfDay } from '@cobliteam/utils/date';
import type React from 'react';
import { useEffect, useState } from 'react';
import TriggerNotificationItem from './TriggerNotificationItem';

const today = new Date();
const limitDay = new Date(new Date().setDate(today.getDate() - 10));

interface TriggerNotificationsListProps {
  unreadNotifications?: number;
  onMarkAllAsRead: () => void;
}

const TriggerNotificationsList = ({ unreadNotifications, onMarkAllAsRead }: TriggerNotificationsListProps) => {
  const { t } = useTranslation('app-header');
  const startOfDayISO = startOfDay(limitDay).toISOString();
  const endOfDayISO = endOfDay(today).toISOString();
  const hasUnreadNotifications = unreadNotifications ? unreadNotifications > 0 : false;

  const { data, isLoading, isError, fetchNextPage, isFetchingNextPage } = useGetTriggersNotifications({
    page: 1,
    size: 10,
    sort: 'created_at,desc',
    startTime: startOfDayISO,
    endTime: endOfDayISO,
  });

  const { mutate, isSuccess: markSuccess, isError: markError } = useMarkAllNotificationsAsRead(startOfDayISO, endOfDayISO);

  const [markAllRead, setMarkAllRead] = useState(!hasUnreadNotifications);

  const triggerNotifications = data?.pages.flatMap((page) => page.content);

  useEffect(() => {
    if (markSuccess) {
      Alert.success(t('alerts.markAllReadSuccess'));
    }

    if (markError) {
      Alert.error(t('alerts.markAllReadError'));
    }
  }, [markSuccess, markError, t]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const element = event.target as HTMLDivElement;
    const isBottom = element.scrollHeight - element.scrollTop <= element.clientHeight;

    if (isBottom && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  if (isLoading) {
    return (
      <div className='flex h-[50vh] items-center justify-center'>
        <Loading />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex h-[50vh] w-full flex-col items-center justify-center gap-2.5'>
        <Icon color='secondary' size='lg' name='message-square-off' className='box-content rounded-full bg-gray-lightest p-3' />
        <Typography variant='body'>{t('notifications.error')}</Typography>
      </div>
    );
  }

  if (!triggerNotifications?.length) {
    return (
      <div className='flex h-[50vh] w-full flex-col items-center justify-center gap-2.5'>
        <Icon color='secondary' size='lg' name='bell' className='box-content rounded-full bg-gray-lightest p-3' />
        <Typography variant='body'>{t('notifications.empty')}</Typography>
      </div>
    );
  }

  return (
    <div className='max-h-[50vh] overflow-auto' onScroll={handleScroll}>
      {hasUnreadNotifications && (
        <div className='p-2.5'>
          <div className='flex w-full items-center justify-between'>
            <Typography variant='caption-bold'>
              {unreadNotifications} {unreadNotifications === 1 ? t('notifications.unread') : t('notifications.unreadPlural')}
            </Typography>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => {
                mutate();
                onMarkAllAsRead();
                setMarkAllRead(true);

                Analytics.track('NavBar - Notification - MarkAllRead');
              }}
            >
              {t('notifications.markAllRead')}
            </Button>
          </div>

          <Divider className='mt-1' />
        </div>
      )}
      {triggerNotifications.map((triggerNotification) => {
        const { id, triggerEvent } = triggerNotification;

        if (triggerEvent) {
          return <TriggerNotificationItem key={id} triggerNotification={triggerNotification} isRead={markAllRead} />;
        }

        return null;
      })}
      {isFetchingNextPage && <Loading />}
    </div>
  );
};

export default TriggerNotificationsList;
